import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DRAG_SENSITIVITY, INERTIA_FRICTION, INERTIA_MIN, INERTIA_START_THRESHOLD, INITIAL_SLIDE_INDEX, LERP_DRAG, LERP_GOTO, LERP_IDLE, POINTER_VELOCITY_BLEND_NEW, POINTER_VELOCITY_BLEND_PREV, POINTER_VELOCITY_MS_TO_FRAME, SCALE_DECAY, SCALE_MIN, SETTLING_EPSILON_PX, SLIDE_Z_INDEX_BASE, SLIDE_Z_INDEX_DISTANCE_CAP, SLIDE_Z_INDEX_STEP, TEAM_SLIDE_SELECTOR } from './const';
import { TrackLayout } from './types';
import { floatIndexToFocusX, focusToFloatIndex, viewportCenterXInTrackSpace } from './utils';
type UseTeamSliderParams = {
  slideCount: number;
  viewportRef: RefObject<HTMLDivElement | null>;
  trackRef: RefObject<HTMLUListElement | null>;
};
export function useTeamSlider({
  slideCount,
  viewportRef,
  trackRef
}: UseTeamSliderParams) {
  // --- layout & transform ---
  const trackLayoutRef = useRef<TrackLayout | null>(null);
  const translateXRef = useRef(0);
  const targetTranslateXRef = useRef(0);

  // --- pointer / gesture ---
  const isDraggingRef = useRef(false);
  const activePointerIdRef = useRef<number | null>(null);
  const pointerDownClientXRef = useRef(0);
  const pointerDownTranslateXRef = useRef(0);
  const lastPointerSampleRef = useRef({
    clientX: 0,
    timeMs: 0
  });

  // --- motion loop ---
  const velocityPxPerFrameRef = useRef(0);
  const isInertiaActiveRef = useRef(false);
  const rafIdRef = useRef(0);
  const tickFnRef = useRef<() => void>(() => {});

  // --- active slide ---
  const activeSlideIndexRef = useRef(0);
  const slideCountRef = useRef(slideCount);
  const hasInitializedTranslateRef = useRef(false);
  const isGlidingToTargetRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(() => Math.min(INITIAL_SLIDE_INDEX, Math.max(0, slideCount - 1)));
  const setActiveIndexRef = useRef(setActiveIndex);
  setActiveIndexRef.current = setActiveIndex;
  slideCountRef.current = slideCount;
  const readTrackLayoutFromDom = (): boolean => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || slideCountRef.current < 1) return false;
    const slideNodes = track.querySelectorAll<HTMLElement>(TEAM_SLIDE_SELECTOR);
    if (!slideNodes.length) return false;
    const viewportWidth = viewport.clientWidth;
    const slideCenterXs = Array.from(slideNodes).map(node => node.offsetLeft + node.offsetWidth / 2);
    const halfViewport = viewportWidth / 2;
    const minTranslateX = halfViewport - slideCenterXs[slideCenterXs.length - 1]!;
    const maxTranslateX = halfViewport - slideCenterXs[0]!;
    trackLayoutRef.current = {
      viewportWidth,
      slideCenterXs,
      minTranslateX,
      maxTranslateX
    };
    return true;
  };
  const clampTranslateX = (value: number): number => {
    const layout = trackLayoutRef.current;
    if (!layout) return value;
    return Math.min(layout.maxTranslateX, Math.max(layout.minTranslateX, value));
  };
  const setTrackTranslateX = (translateX: number) => {
    const track_0 = trackRef.current;
    if (track_0) track_0.style.transform = `translate3d(${translateX}px,0,0)`;
  };
  const measure = useCallback(() => {
    if (!viewportRef.current || !trackRef.current || slideCountRef.current < 1) return;
    if (!readTrackLayoutFromDom()) return;
    const layout_0 = trackLayoutRef.current!;
    if (!hasInitializedTranslateRef.current) {
      hasInitializedTranslateRef.current = true;
      const initialIndex = Math.min(INITIAL_SLIDE_INDEX, Math.max(0, layout_0.slideCenterXs.length - 1));
      const initialTranslateX = layout_0.viewportWidth / 2 - layout_0.slideCenterXs[initialIndex]!;
      translateXRef.current = initialTranslateX;
      targetTranslateXRef.current = initialTranslateX;
    } else {
      targetTranslateXRef.current = clampTranslateX(targetTranslateXRef.current);
      translateXRef.current = clampTranslateX(translateXRef.current);
    }
  }, [viewportRef, trackRef]);
  const writeSlideScaleStyles = (translateX_0: number, layoutBeforeReflow: TrackLayout) => {
    const track_1 = trackRef.current;
    if (!track_1) return;
    const focusXInTrack = viewportCenterXInTrackSpace(layoutBeforeReflow.viewportWidth, translateX_0);
    const fractionalFocusIndex = focusToFloatIndex(focusXInTrack, layoutBeforeReflow.slideCenterXs);
    track_1.querySelectorAll<HTMLElement>(TEAM_SLIDE_SELECTOR).forEach((slideEl, slideIndex) => {
      const distanceFromFocus = Math.abs(slideIndex - fractionalFocusIndex);
      const scale = Math.max(SCALE_MIN, SCALE_DECAY ** distanceFromFocus);
      slideEl.style.setProperty('--slide-scale', String(scale));
      slideEl.style.zIndex = String(Math.round(SLIDE_Z_INDEX_BASE - Math.min(distanceFromFocus, SLIDE_Z_INDEX_DISTANCE_CAP) * SLIDE_Z_INDEX_STEP));
    });
  };
  const syncTranslateAfterScaleReflow = (translateXBeforeReflow: number, layoutBeforeReflow_0: TrackLayout) => {
    const focusXBefore = viewportCenterXInTrackSpace(layoutBeforeReflow_0.viewportWidth, translateXBeforeReflow);
    const preservedFractionalIndex = focusToFloatIndex(focusXBefore, layoutBeforeReflow_0.slideCenterXs);
    writeSlideScaleStyles(translateXBeforeReflow, layoutBeforeReflow_0);

    // Force a synchronous layout pass after mutating slide scales so the next geometry read
    // sees up-to-date offsets. `void` discards the value (side effect only); optional chain
    // covers a missing track ref during teardown.
    void trackRef.current?.offsetHeight;
    if (!readTrackLayoutFromDom()) return;
    const layoutAfterReflow = trackLayoutRef.current!;
    const focusXAfter = floatIndexToFocusX(preservedFractionalIndex, layoutAfterReflow.slideCenterXs);
    const translateXAfter = layoutAfterReflow.viewportWidth / 2 - focusXAfter;
    const delta = translateXAfter - translateXBeforeReflow;
    translateXRef.current = translateXAfter;
    targetTranslateXRef.current = clampTranslateX(targetTranslateXRef.current + delta);
    if (isDraggingRef.current) pointerDownTranslateXRef.current += delta;
    setTrackTranslateX(translateXAfter);
  };
  const updateActiveSlideIndexFromFocus = (translateX_1: number, layout_1: TrackLayout) => {
    const focusXInTrack_0 = viewportCenterXInTrackSpace(layout_1.viewportWidth, translateX_1);
    const fractionalIndex = focusToFloatIndex(focusXInTrack_0, layout_1.slideCenterXs);
    const roundedIndex = Math.min(slideCountRef.current - 1, Math.max(0, Math.round(fractionalIndex)));
    if (roundedIndex !== activeSlideIndexRef.current) {
      activeSlideIndexRef.current = roundedIndex;
      setActiveIndexRef.current(roundedIndex);
    }
  };
  const syncTrackState = (translateX_2: number) => {
    if (!readTrackLayoutFromDom()) return;
    const layoutBefore = trackLayoutRef.current;
    if (!layoutBefore?.slideCenterXs.length) return;
    updateActiveSlideIndexFromFocus(translateX_2, layoutBefore);
    syncTranslateAfterScaleReflow(translateX_2, layoutBefore);
  };
  const tick = useCallback(() => {
    readTrackLayoutFromDom();
    const isDragging = isDraggingRef.current;
    const isInertia = isInertiaActiveRef.current;
    const smoothing = isDragging ? LERP_DRAG : isGlidingToTargetRef.current && !isInertia ? LERP_GOTO : LERP_IDLE;
    if (isInertia && !isDragging) {
      targetTranslateXRef.current += velocityPxPerFrameRef.current;
      targetTranslateXRef.current = clampTranslateX(targetTranslateXRef.current);
      velocityPxPerFrameRef.current *= INERTIA_FRICTION;
      if (Math.abs(velocityPxPerFrameRef.current) < INERTIA_MIN) {
        isInertiaActiveRef.current = false;
        velocityPxPerFrameRef.current = 0;
        syncTrackState(targetTranslateXRef.current);
      }
    }
    targetTranslateXRef.current = clampTranslateX(targetTranslateXRef.current);
    translateXRef.current += (targetTranslateXRef.current - translateXRef.current) * smoothing;
    setTrackTranslateX(translateXRef.current);
    syncTrackState(translateXRef.current);
    if (isGlidingToTargetRef.current && !isDragging && !isInertiaActiveRef.current && Math.abs(targetTranslateXRef.current - translateXRef.current) <= SETTLING_EPSILON_PX) isGlidingToTargetRef.current = false;
    const stillSettling = Math.abs(targetTranslateXRef.current - translateXRef.current) > SETTLING_EPSILON_PX || isDragging || isInertiaActiveRef.current;
    if (stillSettling) rafIdRef.current = requestAnimationFrame(() => tickFnRef.current());
  }, []);
  tickFnRef.current = tick;
  const scheduleNextFrame = useCallback(() => {
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => tickFnRef.current());
  }, []);
  const onPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    isInertiaActiveRef.current = false;
    velocityPxPerFrameRef.current = 0;
    isGlidingToTargetRef.current = false;
    isDraggingRef.current = true;
    activePointerIdRef.current = event.pointerId;
    pointerDownClientXRef.current = event.clientX;
    pointerDownTranslateXRef.current = translateXRef.current;
    targetTranslateXRef.current = translateXRef.current;
    lastPointerSampleRef.current = {
      clientX: event.clientX,
      timeMs: performance.now()
    };
    readTrackLayoutFromDom();
    const viewport_0 = viewportRef.current;
    viewport_0?.setPointerCapture(event.pointerId);
    viewport_0?.setAttribute('data-dragging', 'true');
    scheduleNextFrame();
  }, [scheduleNextFrame, viewportRef]);
  const onPointerMove = useCallback((event_0: React.PointerEvent) => {
    if (!isDraggingRef.current || event_0.pointerId !== activePointerIdRef.current) return;
    event_0.preventDefault();
    readTrackLayoutFromDom();
    const dragDeltaX = (event_0.clientX - pointerDownClientXRef.current) * DRAG_SENSITIVITY;
    targetTranslateXRef.current = clampTranslateX(pointerDownTranslateXRef.current + dragDeltaX);
    const now = performance.now();
    const deltaTime = now - lastPointerSampleRef.current.timeMs;
    if (deltaTime > 0) {
      const velocitySample = (event_0.clientX - lastPointerSampleRef.current.clientX) / deltaTime * POINTER_VELOCITY_MS_TO_FRAME * DRAG_SENSITIVITY;
      velocityPxPerFrameRef.current = velocityPxPerFrameRef.current * POINTER_VELOCITY_BLEND_PREV + velocitySample * POINTER_VELOCITY_BLEND_NEW;
    }
    lastPointerSampleRef.current = {
      clientX: event_0.clientX,
      timeMs: now
    };
    scheduleNextFrame();
  }, [scheduleNextFrame]);
  const endDrag = useCallback((event_1?: React.PointerEvent) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    activePointerIdRef.current = null;
    if (event_1) viewportRef.current?.releasePointerCapture(event_1.pointerId);
    viewportRef.current?.removeAttribute('data-dragging');
    if (Math.abs(velocityPxPerFrameRef.current) > INERTIA_START_THRESHOLD) isInertiaActiveRef.current = true;else syncTrackState(targetTranslateXRef.current);
    scheduleNextFrame();
  }, [scheduleNextFrame, viewportRef]);
  const onPointerUp = useCallback((event_2: React.PointerEvent) => {
    if (event_2.pointerId !== activePointerIdRef.current) return;
    endDrag(event_2);
  }, [endDrag]);
  const onPointerCancel = useCallback((event_3: React.PointerEvent) => {
    endDrag(event_3);
  }, [endDrag]);
  const goToSlide = useCallback((nextIndex: number) => {
    if (slideCountRef.current < 2) return;
    if (nextIndex < 0 || nextIndex >= slideCountRef.current) return;
    isInertiaActiveRef.current = false;
    velocityPxPerFrameRef.current = 0;
    isDraggingRef.current = false;
    if (!readTrackLayoutFromDom()) return;
    const layout_2 = trackLayoutRef.current;
    if (!layout_2?.slideCenterXs.length) return;
    const translateX_3 = clampTranslateX(layout_2.viewportWidth / 2 - layout_2.slideCenterXs[nextIndex]!);
    isGlidingToTargetRef.current = true;
    targetTranslateXRef.current = translateX_3;
    scheduleNextFrame();
  }, [scheduleNextFrame]);
  useLayoutEffect(() => {
    hasInitializedTranslateRef.current = false;
    const frameId = requestAnimationFrame(() => {
      measure();
      if (trackLayoutRef.current?.slideCenterXs.length) {
        setTrackTranslateX(translateXRef.current);
        syncTrackState(translateXRef.current);
        scheduleNextFrame();
      }
    });
    return () => cancelAnimationFrame(frameId);
  }, [slideCount, measure, scheduleNextFrame]);
  useEffect(() => {
    const viewport_1 = viewportRef.current;
    const resizeObserver = new ResizeObserver(() => {
      measure();
      setTrackTranslateX(translateXRef.current);
      syncTrackState(translateXRef.current);
      scheduleNextFrame();
    });
    if (viewport_1) resizeObserver.observe(viewport_1);
    window.addEventListener('resize', measure);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measure);
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [measure, scheduleNextFrame, viewportRef]);
  return {
    activeIndex,
    goToSlide,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel
  };
}